/**
 * Deployment Analytics
 * Build/release frequency, success/failure rate, rollbacks, environment stability, CI/CD pipeline analytics
 */

import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  MdRocketLaunch,
  MdCheckCircle,
  MdError,
  MdRefresh,
  MdCloud,
  MdTrendingUp,
} from 'react-icons/md';

// Mock data with realistic values
const buildFrequencyData = [
  { week: 'Week 1', builds: 45, releases: 12 },
  { week: 'Week 2', builds: 52, releases: 15 },
  { week: 'Week 3', builds: 38, releases: 10 },
  { week: 'Week 4', builds: 65, releases: 18 },
  { week: 'Week 5', builds: 42, releases: 14 },
  { week: 'Week 6', builds: 58, releases: 16 },
];

const deploymentSuccessData = [
  { month: 'Jan', success: 98, failure: 2 },
  { month: 'Feb', success: 97, failure: 3 },
  { month: 'Mar', success: 99, failure: 1 },
  { month: 'Apr', success: 96, failure: 4 },
  { month: 'May', success: 98.5, failure: 1.5 },
  { month: 'Jun', success: 99.2, failure: 0.8 },
];

const rollbackData = [
  { month: 'Jan', rollbacks: 1 },
  { month: 'Feb', rollbacks: 2 },
  { month: 'Mar', rollbacks: 0 },
  { month: 'Apr', rollbacks: 3 },
  { month: 'May', rollbacks: 1 },
  { month: 'Jun', rollbacks: 0 },
];

const environmentStability = [
  { environment: 'Production', stability: 99.9, uptime: 99.99, deployments: 45 },
  { environment: 'UAT', stability: 98.5, uptime: 99.5, deployments: 82 },
  { environment: 'Test', stability: 95.2, uptime: 98.2, deployments: 156 },
  { environment: 'Dev', stability: 92.4, uptime: 97.8, deployments: 312 },
];

const cicdPipelineData = [
  { pipeline: 'Build Pipeline', success: 99.8, avgTime: 4.2, lastRun: '2 mins ago' },
  { pipeline: 'Test Pipeline', success: 94.5, avgTime: 12.5, lastRun: '5 mins ago' },
  { pipeline: 'Deploy Pipeline', success: 99.2, avgTime: 8.4, lastRun: '15 mins ago' },
  { pipeline: 'Integration Tests', success: 88.4, avgTime: 45.0, lastRun: '1 hour ago' },
];

const deploymentStatusData = [
  { status: 'Success', count: 145, color: '#10b981' },
  { status: 'Failed', count: 12, color: '#ef4444' },
  { status: 'Rolled Back', count: 7, color: '#f59e0b' },
];

export function DeploymentAnalytics() {
  const totalDeployments = deploymentStatusData.reduce((sum, item) => sum + item.count, 0);
  const successRate = ((deploymentStatusData.find((s) => s.status === 'Success')?.count || 0) / totalDeployments * 100).toFixed(1);
  const avgRollbacks = (rollbackData.reduce((sum, item) => sum + item.rollbacks, 0) / rollbackData.length).toFixed(1);

  return (
    <div className="w-full space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Deployment Success Rate</p>
            <MdCheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{successRate}%</p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">No data available</p>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Total Deployments</p>
            <MdRocketLaunch className="w-5 h-5 text-[#0088cc]" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{totalDeployments}</p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">Last 6 months</p>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Avg Rollbacks/Month</p>
            <MdRefresh className="w-5 h-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{avgRollbacks}</p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">No data available</p>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Builds This Week</p>
            <MdTrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
            {buildFrequencyData[buildFrequencyData.length - 1]?.builds || 0}
          </p>
          <p className="text-xs text-[hsl(var(--foreground))] font-semibold mt-1">No data available</p>
        </div>
      </div>

      {/* Build & Release Frequency */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Build & Release Frequency</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Weekly build and release counts</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={buildFrequencyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip />
            <Legend />
            <Bar dataKey="builds" fill="#3b82f6" name="Builds" />
            <Bar dataKey="releases" fill="#10b981" name="Releases" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Deployment Success/Failure Rate */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Deployment Success/Failure Rate</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Monthly deployment outcomes</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={deploymentSuccessData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="success" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Success %" />
            <Area type="monotone" dataKey="failure" stackId="2" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} name="Failure %" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Rollback Count */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Rollback Count</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Monthly rollback trends</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={rollbackData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip />
            <Line type="monotone" dataKey="rollbacks" stroke="#f59e0b" strokeWidth={2} name="Rollbacks" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Environment Stability Score */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Environment Stability Score</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Stability metrics by environment</p>
          </div>
          <MdCloud className="w-6 h-6 text-[#0088cc]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {environmentStability.map((env, index) => (
            <div key={index} className="p-4 rounded-lg bg-[hsl(var(--secondary))]">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[hsl(var(--foreground))]">{env.environment}</h4>
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 dark:bg-emerald-900 text-[hsl(var(--foreground))] font-semibold">
                  {env.stability}%
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Uptime:</span>
                  <span className="font-medium text-[hsl(var(--foreground))]">{env.uptime}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Deployments:</span>
                  <span className="font-medium text-[hsl(var(--foreground))]">{env.deployments}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CI/CD Pipeline Analytics */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">CI/CD Pipeline Analytics</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Pipeline performance metrics</p>
          </div>
        </div>
        <div className="space-y-3">
          {cicdPipelineData.map((pipeline, index) => (
            <div key={index} className="p-4 rounded-lg bg-[hsl(var(--secondary))]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-[hsl(var(--foreground))]">{pipeline.pipeline}</h4>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Last run: {pipeline.lastRun}</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-right">
                    <p className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Success Rate</p>
                    <p className="font-bold text-emerald-600">{pipeline.success}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">Avg Time</p>
                    <p className="font-bold text-[hsl(var(--foreground))]">{pipeline.avgTime} min</p>
                  </div>
                </div>
              </div>
              <div className="w-full bg-[#0088cc]/15 dark:bg-[#0088cc]/20 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-[#0088cc]"
                  style={{ width: `${pipeline.success}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deployment Status Distribution */}
      <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Deployment Status Distribution</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Overall deployment outcomes</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={deploymentStatusData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={100}
              fill="#8884d8"
              dataKey="count"
              label={({ status, count, percent }) => `${status}: ${count} (${(percent * 100).toFixed(0)}%)`}
            >
              {deploymentStatusData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}





